import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { ExploreItemProps } from "../../screens/ExplorePanel/interfaces/ExploreSection";
import {
  getFavoritesFromStorage,
  saveFavoritesToStorage,
} from "../../utils/favoritesStorage";
import { AuthContext } from "../../App";

interface FavoritesContextInterface {
  favorites: ExploreItemProps[];
  addToFavorites: (item: ExploreItemProps) => void;
  removeFromFavorites: (itemId: string) => void;
  toggleFavorite: (item: ExploreItemProps) => void;
  isFavorite: (itemId: string) => boolean;
  clearFavorites: () => void;
}

export const FavoritesContext = createContext<FavoritesContextInterface>({
  favorites: [],
  addToFavorites: () => null,
  removeFromFavorites: () => null,
  toggleFavorite: () => null,
  isFavorite: () => false,
  clearFavorites: () => null,
});

interface ProviderInterface {
  children: React.ReactNode;
}

export default function FavoritesProvider({ children }: ProviderInterface) {
  const [favorites, setFavorites] = useState<ExploreItemProps[]>([]);
  const { user } = useContext(AuthContext);
  const previousUserId = useRef<string | null>(null);
  const isChangingUser = useRef<boolean>(false);
  
  // Load favorites from localStorage when initializing. If user is not set, wait for it to be set.
  useEffect(() => {
    const currentUserId = user?.uuid_user;
    
    // Check if user has changed
    if (previousUserId.current !== currentUserId) {
      // Set flag to prevent saving during user change
      isChangingUser.current = true;
      
      // Reset favorites immediately when user changes
      setFavorites([]);
      previousUserId.current = currentUserId || null;
    }
    
    if (currentUserId) {
      const savedFavorites = getFavoritesFromStorage(currentUserId);
      if (savedFavorites) {
        setFavorites(savedFavorites);
      }
      // Clear the flag after loading favorites
      isChangingUser.current = false;
    } else {
      // Reset favorites when user is not set (logout scenario)
      setFavorites([]);
      isChangingUser.current = false;
    }
  }, [user?.uuid_user]);

  // Save favorites to localStorage when they change, but only if not changing users
  useEffect(() => {
    if (user?.uuid_user && !isChangingUser.current) {
      saveFavoritesToStorage(favorites, user.uuid_user);
    }
  }, [favorites, user?.uuid_user]);

  const addToFavorites = (item: ExploreItemProps) => {
    setFavorites((prevFavorites) => {
      // Avoid duplicates
      if (prevFavorites.some((fav) => fav.id === item.id)) {
        return prevFavorites;
      }
      return [...prevFavorites, { ...item, liked: true }];
    });
  };

  const removeFromFavorites = (itemId: string) => {
    setFavorites((prevFavorites) =>
      prevFavorites.filter((fav) => fav.id !== itemId)
    );
  };

  const toggleFavorite = (item: ExploreItemProps) => {
    const isCurrentlyFavorite = favorites.some((fav) => fav.id === item.id);
    
    if (isCurrentlyFavorite) {
      removeFromFavorites(item.id);
    } else {
      addToFavorites(item);
    }
  };

  const isFavorite = (itemId: string): boolean => {
    return favorites.some((fav) => fav.id === itemId);
  };

  const clearFavorites = () => {
    setFavorites([]);
  };

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        addToFavorites,
        removeFromFavorites,
        toggleFavorite,
        isFavorite,
        clearFavorites,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}
